import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IApiError } from '@models';
import { AlertToastService, BreakpointService } from '@services';
import {
  clearField,
  getError,
  getStringLength,
  hasFailedEntities,
  hasSingleError,
  trimMaxLength,
  trimSpaces,
  Unsubscriber,
} from '@shared';
import * as moment from 'moment/moment';
import { BehaviorSubject, distinctUntilChanged, finalize, map, pipe, startWith } from 'rxjs';
import { InvoiceRegister } from '../../../../models/invoice/invoice-register.model';
import { InvoiceRegisterApiService } from '../../../../services/registers/invoice-register.api.service';
import { NewRegisterModalPayload, NewRegisterMode } from './new-register.constants';
import {
  PartialSuccessService,
  PartialSuccessResponse,
  RegisterInvoicesInsertPartialSuccessConfig,
} from '../../partial-success';
import { INewRegisterModalClosePayload } from './new-register.models';

const descriptionErrors = {
  maxlength: 'Максимальна довжина 30 символів',
  pattern: 'Містить недопустимий символ',
};

@Component({
  selector: 'app-new-register',
  templateUrl: './new-register.component.html',
  styleUrls: ['./new-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewRegisterComponent extends Unsubscriber implements OnInit {
  readonly descriptionErrors = descriptionErrors;
  readonly getError = getError;
  readonly newRegisterName: string = 'new';
  readonly pattern = /^[а-яА-Я,ёЁіІїЇєЄґҐюЮa-zA-Z\d\s\.№;!:/\-'\*\+#`',\/\(\)"«»\\\\_&„""]*$/ui;
  private mode: NewRegisterMode = NewRegisterMode.NEW;

  private _pending = new BehaviorSubject(false);
  pending$ = this._pending.asObservable();

  set pending(value: boolean) {
    this._pending.next(value);
  }

  registers: InvoiceRegister[] = [];

  invoicesIds: string[] = [];

  minDate = moment().toDate();

  dateValue = new Date();

  maxLength = 30;

  maxInputLength = 50;

  form: FormGroup;

  closeParams: Record<string, boolean> | null = null;

  isDesktop$ = this.breakpointService.isDesktop$;
  isMobile$ = this.breakpointService.isMobile$;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: NewRegisterModalPayload,
    public dialogRef: MatDialogRef<NewRegisterComponent>,
    private invoiceRegisterService: InvoiceRegisterApiService,
    private alertToastService: AlertToastService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<NativeDateAdapter>,
    private partialSuccess: PartialSuccessService,
    private breakpointService: BreakpointService,
  ) {
    super();
    dateAdapter.setLocale('uk-UA');
  }

  get title(): string {
    return this.data.title ?? 'Додати до реєстру';
  }

  get canViewDescription(): boolean {
    return this.data.canViewDescription ?? true;
  }

  get canViewDate(): boolean {
    return this.data.canViewDate ?? true;
  }

  get description(): AbstractControl {
    return this.form?.get('description') as AbstractControl;
  }

  get registerRef(): AbstractControl {
    return this.form?.get('registerRef') as AbstractControl;
  }

  get date(): AbstractControl {
    return this.form?.get('date') as AbstractControl;
  }

  onDateChange($event): void {
    this.date.setValue(new Date($event));
  }

  onBlur($event: FocusEvent): void {
    let value = ($event.target as HTMLInputElement).value;
    this.checkMaxStringLength(value, true);
  }

  private initCheckMaxStringLengthSubscription(): void {
    this.subscriptions = this.description?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value: string) => this.checkMaxStringLength(value));
  }

  private checkMaxStringLength(value: string, shouldTrimSpaces: boolean = false): void {
    if (!this.description) {
      return;
    }

    const cleanValue = shouldTrimSpaces ? trimSpaces(value) : value;
    if (getStringLength(trimSpaces(value)) > this.maxInputLength) {
      this.description?.setValue(trimMaxLength(trimSpaces(value), this.maxInputLength));
    } else {
      this.description?.setValue(cleanValue);
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.initRegisters();
    this.setMode();
    this.initCheckMaxStringLengthSubscription();
  }

  setMode(): void {
    this.mode = this.data.newRegisterMode;
  }

  private initRegisters(): void {
    if (this.registers.length === 0 && this.data.getRegistersFromApi) {
      this.getRegistries();
    }
  }

  registerDescription(register: InvoiceRegister): string {
    const parts: string[] = [];

    parts.push(register.docNumber || '&mdash;');

    if (!!register.dateTime) {
      parts.push('від ' + moment(register.dateTime ?? '').format('DD.MM.YYYY'));
    }

    if (register.description) {
      parts.push(`(${register.description})`);
    }

    return parts.join(' ');
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.pending = true;

    if (this.mode === NewRegisterMode.EMPTY) {
      if (this.registerRef.value === this.newRegisterName) {
        this.createEmptyRegister();
      } else {
        this.dialogRef.close({ register: this.registerRef.value, type: NewRegisterMode.EXIST, success: true });
      }
    } else {
      this.registerRef.value === this.newRegisterName
        ? this.addToNewRegister()
        : this.insertIntoExistRegister();
    }
  }

  private createEmptyRegister(): void {
    this.invoiceRegisterService.createEmptyRegister(this.date.value, this.description.value)
      .pipe(finalize(() => (this.pending = false)))
      .subscribe(
        (register) => {
          this.dialogRef.close({ register, type: NewRegisterMode.NEW, success: true });
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.dialogRef.close({ register: null, type: NewRegisterMode.NEW, success: true });
        },
      );
  }

  private initForm(): void {
    this.form = this.fb.group({
      registerRef: [this.newRegisterName],
      description: ['', [Validators.maxLength(30), Validators.pattern(this.pattern)]],
      date: [new Date(), Validators.required],
    });

    if (this.data.canEditDescription) {
      this.description.enable();
      this.subscribeToRegisterRefChanges();
    } else {
      this.description.disable();
    }

    if (this.data.canEditDate) {
      this.date.enable();
    } else {
      this.date.disable();
    }
  }

  private getRegistries(): void {
    this.pending = true;

    this.invoiceRegisterService.getList({ isOpen: false }, null)
      .pipe(
        finalize(() => this.pending = false),
        map((data: any) => data.list),
      ).subscribe((registers: InvoiceRegister[]) => {
      this.registers = registers.filter((register) => register.printed === '0');
    });
  }

  private subscribeToRegisterRefChanges(): void {
    this.subscriptions = this.registerRef?.valueChanges
      .pipe(startWith(this.newRegisterName), pipe(distinctUntilChanged()))
      .subscribe((value) => {
        if (value === this.newRegisterName) {
          if (this.data.canEditDescription) {
            this.description.enable();
            clearField(this.description, null, '');
          } else {
            this.description.disable();
          }

          if (this.data.canEditDate) {
            this.date.enable();
          } else {
            this.date.disable();
          }

          this.date.setValue(new Date());
        } else {
          clearField(this.description, null, '');
          this.description.setValue(this.registerRef.value.description);
          this.date.setValue(this.registerRef.value.dateTime);
          this.description.disable();
          this.date.disable();
        }
      });
  }

  clearField(): void {
    this.description?.setValue('');
  }

  private addToNewRegister(): void {
      this.invoiceRegisterService
        .addWithInvoices(this.description.value, this.invoicesIds, this.date.value)
        .pipe(finalize(() => this.pending = false))
        .subscribe((res: PartialSuccessResponse) => this.handleResponse(res),
          (errors: IApiError[]) => {
            if (errors[0]) {
              this.alertToastService.pushApiErrors(errors);
            }
            this.dialogRef.close({ register: null, type: NewRegisterMode.NEW, success: true });
          },
        );
  }

  private handleResponse(response: PartialSuccessResponse): void {
    if (!response) {
      this.dialogRef.close({ success: false, partialSuccess: false });
      return;
    }

    if (hasFailedEntities(response)) {
      if (hasSingleError(response)) {
        this.alertToastService.pushError('Помилка при додаванні');
        this.dialogRef.close({ success: false, partialSuccess: false });
        return;
      }

      const config = {
        ...RegisterInvoicesInsertPartialSuccessConfig,
        label: response.Barcode ? `№${response.Barcode}` : '',
      };
      this.partialSuccess.open(response, config);
      this.dialogRef.close({
        Barcode: response.Barcode,
        Ref: response.Ref,
        success: true,
        partialSuccess: true,
      } as INewRegisterModalClosePayload);
    } else {
      this.alertToastService.pushSuccess('Успішно додано до реєстру');
      this.closeParams = { success: true, partialSuccess: false };
      this.dialogRef.close({
        Barcode: response.Barcode,
        Ref: response.Ref,
        success: true,
        partialSuccess: false,
      } as INewRegisterModalClosePayload);
    }
  }

  private insertIntoExistRegister(): void {
    this.invoiceRegisterService
      .insertDocuments(this.registerRef.value.id, this.invoicesIds)
      .pipe(finalize(() => (this.pending = false)))
      .subscribe(
        (response: PartialSuccessResponse) => {
          this.handleResponse(response);
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          // Here we push NewRegisterMode.NEW to trigger reload of the registers list
          this.dialogRef.close({ register: null, type: NewRegisterMode.NEW, success: true });
        },
      );
  }
}
